/**
 * Customer optional use of kiosk-gated SSE (`GET /events/:salesPointId`).
 * Under ENFORCE that route requires realtime_device_transport AND
 * (surface_kiosk OR surface_customer) — SSOT AssertKioskRealtimeTransportEntitledUseCase.
 * G3a: fail-closed when those entitlements deny (defense-in-depth). Payment-only
 * seed may enable both surfaces + realtime; client skip is for true
 * unentitled/drift tenants, not a product carve-out that requires surface_kiosk alone.
 * EventSource cannot read HTTP status — probe with fetch after error and
 * fail-closed on 403 so reconnect cannot spam.
 *
 * G2 — default transport is {@link stormAwareFetch} so same-origin `/api/*`
 * probes enqueue (fair-queue spacing) and honor the HTTP storm latch.
 * `installCustomerHttpStormFetchGuard` remains latch-only; do not rely on it
 * for queueing.
 */

import { stormAwareFetch } from '../http/stormAwareFetch.js';

export type CustomerKioskSseAccess = 'allowed' | 'denied' | 'unknown';

/**
 * Probe kiosk SSE entitlement without leaving a live EventSource open.
 * Any 403 on this route fails closed (defense-in-depth for unentitled/drift
 * tenants — not a payment-only bookstore realtime carve-out).
 *
 * @param fetchImpl - Optional override (unit tests). Default: stormAwareFetch.
 */
export async function probeCustomerKioskSseAccess(
  sseUrl: string,
  fetchImpl: typeof fetch = stormAwareFetch,
): Promise<CustomerKioskSseAccess> {
  const controller = new AbortController();
  try {
    const response = await fetchImpl(sseUrl, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      credentials: 'include',
      signal: controller.signal,
    });

    if (response.status === 403) {
      // Any 403 on this kiosk-gated route fails closed (entitlement or auth).
      try {
        await response.json();
      } catch {
        // ignore non-JSON bodies
      }
      return 'denied';
    }

    if (response.ok) {
      controller.abort();
      try {
        await response.body?.cancel();
      } catch {
        // ignore cancel races after abort
      }
      return 'allowed';
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}
